import React from 'react';
const { useState } = React;
import { COACHING_MODALITIES, COACHING_FOCUS, COACHING_SPECIALTIES, MAX_GOALS, MAX_SEQUENCE, label } from '../../coachingConfig.js';
import { inputSt, textareaSt, labelSt, mutedSt, Field } from '../diagnostic/shared.jsx';
import { MoreOptions, Row } from './shared.jsx';

// Program editor: the templates (Programas) and the adapted copy of one process.
// Essential fields first; activities, tools, review and closing grouped below.
// `fixed` locks modality and focus (a process: the focus changes only with a new agreement).

const listToText = (l) => (l || []).join('\n');
const textToList = (t) => t.split('\n').map(x => x.trim()).filter(Boolean);

export default function ProgramEditor({ value, onChange, fixed = false, tools = [] }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const content = value.content || { goals: [], sequence: [], activities: [], tools: [], review: '', closing: '' };
  const setContent = (k, v) => onChange({ ...value, content: { ...content, [k]: v } });
  const seq = content.sequence || [];
  const setStep = (i, patch) => setContent('sequence', seq.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const move = (i, d) => { const next = [...seq]; const [x] = next.splice(i, 1); next.splice(i + d, 0, x); setContent('sequence', next.map((s, j) => ({ ...s, n: j + 1 }))); };
  const team = value.modality === 'team';
  const toggleSpec = (k) => set('specialties', (value.specialties || []).includes(k) ? value.specialties.filter(x => x !== k) : [...(value.specialties || []), k]);
  const counts = { group: seq.filter(s => s.kind === 'group').length, individual: seq.filter(s => s.kind !== 'group').length };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Field label="Nombre"><input style={inputSt} value={value.name || ''} maxLength={120} onChange={e => set('name', e.target.value)} /></Field>
      <Field label="Descripción"><textarea style={textareaSt} value={value.description || ''} maxLength={2000} onChange={e => set('description', e.target.value)} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <Field label="Modalidad">
          <select style={inputSt} value={value.modality} disabled={fixed} onChange={e => set('modality', e.target.value)}>
            {COACHING_MODALITIES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </Field>
        <Field label={fixed ? 'Foco (se cambia con un nuevo acuerdo)' : 'Foco propuesto'}>
          <select style={inputSt} value={value.focus} disabled={fixed} onChange={e => set('focus', e.target.value)}>
            {COACHING_FOCUS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </Field>
        <Field label="Duración (semanas)"><input type="number" min={1} max={104} style={inputSt} value={value.durationWeeks || ''} onChange={e => set('durationWeeks', Number(e.target.value))} /></Field>
        <Field label="Minutos por sesión"><input type="number" min={15} max={240} style={inputSt} value={value.sessionMinutes || ''} onChange={e => set('sessionMinutes', Number(e.target.value))} /></Field>
      </div>
      <div style={mutedSt}>
        {seq.length} sesiones{team ? ` · ${counts.group} grupales y ${counts.individual} individuales por integrante` : ''}. La cantidad sale de la secuencia.
      </div>

      <Field label="Objetivos orientativos (uno por línea)" hint={`Hasta ${MAX_GOALS}. Los objetivos de cada proceso se acuerdan en la primera sesión.`}>
        <textarea style={textareaSt} value={listToText(content.goals)} onChange={e => setContent('goals', textToList(e.target.value).slice(0, MAX_GOALS))} />
      </Field>

      <div>
        <span style={labelSt}>Secuencia de sesiones</span>
        <div style={{ display: 'grid', gap: 8 }}>
          {seq.map((s, i) => (
            <div key={i} style={{ border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10, display: 'grid', gap: 6 }}>
              <Row>
                <b style={{ fontSize: 12, minWidth: 22 }}>{i + 1}.</b>
                {team && (
                  <select style={{ ...inputSt, width: 'auto' }} value={s.kind} onChange={e => setStep(i, { kind: e.target.value })}>
                    <option value="group">Grupal</option><option value="individual">Individual</option>
                  </select>
                )}
                <input style={{ ...inputSt, flex: 1, width: 'auto' }} value={s.title} maxLength={120} placeholder="Título" onChange={e => setStep(i, { title: e.target.value })} />
                <button className="action-btn" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                <button className="action-btn" disabled={i === seq.length - 1} onClick={() => move(i, 1)}>↓</button>
                <button className="action-btn" onClick={() => setContent('sequence', seq.filter((_, j) => j !== i).map((x, j) => ({ ...x, n: j + 1 })))}>✕</button>
              </Row>
              <textarea style={{ ...textareaSt, minHeight: 40 }} value={s.purpose || ''} maxLength={800} placeholder="Para qué sirve esta sesión" onChange={e => setStep(i, { purpose: e.target.value })} />
              <MoreOptions title="Actividades entre sesiones y herramientas">
                <Field label="Actividades después de esta sesión (una por línea)">
                  <textarea style={{ ...textareaSt, minHeight: 40 }} value={listToText(s.between)} onChange={e => setStep(i, { between: textToList(e.target.value).slice(0, 5) })} />
                </Field>
                {tools.length > 0 && (
                  <Field label="Herramientas sugeridas de la biblioteca">
                    <select multiple style={{ ...inputSt, minHeight: 70 }} value={s.tools || []} onChange={e => setStep(i, { tools: [...e.target.selectedOptions].map(o => o.value).slice(0, 10) })}>
                      {tools.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                    </select>
                  </Field>
                )}
              </MoreOptions>
            </div>
          ))}
        </div>
        <Row style={{ marginTop: 8 }}>
          <button className="action-btn" disabled={seq.length >= MAX_SEQUENCE} onClick={() => setContent('sequence', [...seq, { n: seq.length + 1, kind: 'individual', title: `Sesión ${seq.length + 1}`, purpose: '', tools: [], between: [] }])}>+ Sesión individual</button>
          {team && <button className="action-btn" disabled={seq.length >= MAX_SEQUENCE} onClick={() => setContent('sequence', [...seq, { n: seq.length + 1, kind: 'group', title: `Sesión grupal ${seq.length + 1}`, purpose: '', tools: [], between: [] }])}>+ Sesión grupal</button>}
        </Row>
      </div>

      <MoreOptions title="Especialidades, actividades, herramientas, revisión y cierre">
        {!fixed && (
          <div>
            <span style={labelSt}>Especialidades (solo si el cliente las pide)</span>
            <Row>{COACHING_SPECIALTIES.map(s => (
              <label key={s.key} style={{ fontSize: 13 }}><input type="checkbox" checked={(value.specialties || []).includes(s.key)} onChange={() => toggleSpec(s.key)} /> {s.label}</label>
            ))}</Row>
          </div>
        )}
        <Field label="Actividades y materiales entre sesiones (uno por línea)">
          <textarea style={textareaSt} value={listToText(content.activities)} onChange={e => setContent('activities', textToList(e.target.value).slice(0, 20))} />
        </Field>
        {tools.length > 0 && (
          <Field label="Herramientas sugeridas del programa" hint="Solo herramientas activas de la biblioteca.">
            <select multiple style={{ ...inputSt, minHeight: 90 }} value={content.tools || []} onChange={e => setContent('tools', [...e.target.selectedOptions].map(o => o.value).slice(0, 30))}>
              {tools.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          </Field>
        )}
        <Field label="Cómo se revisan los avances"><textarea style={textareaSt} value={content.review || ''} maxLength={2000} onChange={e => setContent('review', e.target.value)} /></Field>
        <Field label="Cómo se cierra el proceso"><textarea style={textareaSt} value={content.closing || ''} maxLength={2000} onChange={e => setContent('closing', e.target.value)} /></Field>
      </MoreOptions>
      {fixed && <div style={mutedSt}>Foco: {label(COACHING_FOCUS, value.focus)}. Los cambios acá solo afectan a este proceso, nunca a la plantilla.</div>}
    </div>
  );
}
